import React from 'react';
import Markdown from 'react-markdown';
import type { Problem } from '../types/problem';

interface ProblemPanelProps {
  problem: Problem | null;
  onToggle?: () => void;
}

export const ProblemPanel: React.FC<ProblemPanelProps> = ({
  problem,
  onToggle,
}) => {
  const handlePanelClick = (e: React.MouseEvent) => {
    const selection = window.getSelection();
    if (e.target === e.currentTarget && (!selection || selection.toString().length === 0)) {
      onToggle?.();
    }
  };

  const getDifficultyColor = (difficulty: Problem['difficulty']) => {
    switch (difficulty) {
      case 'easy': return 'var(--success)';
      case 'medium': return 'var(--warning)';
      case 'hard': return 'var(--destructive)';
    }
  };

  return (
    <div
      className="panel problem-panel problem-panel-container"
      onClick={handlePanelClick}
    >
      {/* Problem Picker removed for Dashboard approach */}

      {problem ? (
        <>
          <div className="problem-header">
            <h1 className="problem-title">{problem.title}</h1>
            <span
              className="difficulty-badge"
              style={{ color: getDifficultyColor(problem.difficulty) }}
            >
              {problem.difficulty}
            </span>
          </div>

          {/* Render description as formatted text */}
          <div className="markdown-content">
            <Markdown>{problem.description}</Markdown>
          </div>

          {/* Examples */}
          {problem.examples && problem.examples.length > 0 && (
            <div className="examples-section">
              <h3 className="examples-title">Examples</h3>
              {problem.examples.map((example, idx) => (
                <div key={idx} className="example-card">
                  <div className="example-code-block">
                    <div className="example-io-line">
                      <span className="example-label">Input:</span>{' '}
                      <code>{problem.functionName}({example.args.map(arg => JSON.stringify(arg)).join(', ')})</code>
                    </div>
                    <div style={{ marginBottom: example.explanation ? '0.5rem' : 0 }}>
                      <span className="example-label">Output:</span>{' '}
                      <code className="example-value">{JSON.stringify(example.expected)}</code>
                    </div>
                  </div>
                  {example.explanation && (
                    <div className="example-explanation">
                      <span className="explanation-label">Explanation:</span> {example.explanation}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <p>Loading problem...</p>
      )}
    </div>
  );
};